import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Divider,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    Upload,
    Cancel,
    ExpandMore,
    HelpOutline
} from '@mui/icons-material';
import axios from 'axios';

const BulkMCQImport = ({ open, onClose, tests, onImportComplete }) => {
    const [bulkText, setBulkText] = useState('');
    const [selectedTestId, setSelectedTestId] = useState('');
    const [difficulty, setDifficulty] = useState('medium');
    const [category, setCategory] = useState('General');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [parsedMCQs, setParsedMCQs] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    const exampleFormat = `Example 1:
Q1. What is the capital of France?
A) London
B) Berlin
C) Paris
D) Madrid
Correct: C
Explanation: Paris is the capital and largest city of France.

Example 2:
Q2. Which planet is known as the Red Planet?
a) Venus
b) Mars
c) Jupiter
d) Saturn
Answer: b
Explanation: Mars is called the Red Planet due to its reddish appearance.
`;

    const parseMCQText = (text) => {
        const mcqs = [];
        const sections = text.split(/\n\s*\n/).filter(section => section.trim());

        for (const section of sections) {
            try {
                const lines = section.split('\n').map(line => line.trim()).filter(line => line);

                if (lines.length < 5) continue; // Need at least question + 3 options + correct answer

                let question = '';
                let options = [];
                let correctAnswer = -1;
                let explanation = '';

                // Find question line
                const questionLine = lines.find(line =>
                    line.match(/^(Q\d+\.?|Question:?)/i) ||
                    (!line.match(/^[A-Za-z0-9]\)|^\d+\./) && !line.match(/^(correct|answer|explanation)/i))
                );

                if (questionLine) {
                    question = questionLine.replace(/^(Q\d+\.?\s*|Question:?\s*)/i, '').trim();
                }

                // Find option lines
                for (const line of lines) {
                    // Match patterns like: A) text, a) text, 1. text, 1) text
                    const optionMatch = line.match(/^([A-Za-z0-9])[.)]\s*(.+)$/);
                    if (optionMatch) {
                        options.push({
                            text: optionMatch[2].trim(),
                            imageUrl: '',
                            width: '',
                            height: ''
                        });
                    }
                }

                // Find correct answer
                const correctLine = lines.find(line =>
                    line.match(/^(correct|answer)/i)
                );

                if (correctLine) {
                    const correctMatch = correctLine.match(/^(correct|answer):?\s*([A-Za-z0-9])/i);
                    if (correctMatch) {
                        const correctLetter = correctMatch[2].toUpperCase();
                        // Convert letter/number to index
                        if (correctLetter.match(/[A-Z]/)) {
                            correctAnswer = correctLetter.charCodeAt(0) - 'A'.charCodeAt(0);
                        } else if (correctLetter.match(/\d/)) {
                            correctAnswer = parseInt(correctLetter) - 1;
                        }
                    }
                }

                // Find explanation
                const explanationLine = lines.find(line =>
                    line.match(/^explanation/i)
                );

                if (explanationLine) {
                    explanation = explanationLine.replace(/^explanation:?\s*/i, '').trim();
                }

                // Validate parsed MCQ
                if (question && options.length >= 3 && correctAnswer >= 0 && correctAnswer < options.length) {
                    mcqs.push({
                        question,
                        options,
                        correctAnswer,
                        explanation,
                        difficulty,
                        category
                    });
                }
            } catch (error) {
                console.error('Error parsing MCQ section:', error);
            }
        }

        return mcqs;
    };

    const handlePreview = () => {
        if (!bulkText.trim()) {
            setError('Please enter MCQ text to preview');
            return;
        }

        const parsed = parseMCQText(bulkText.trim());
        if (parsed.length === 0) {
            setError('No valid MCQs found. Please check the format and try again.');
            return;
        }

        setParsedMCQs(parsed);
        setShowPreview(true);
        setError('');
    };

    const handleImport = async () => {
        if (!selectedTestId) {
            setError('Please select a test to import MCQs into');
            return;
        }

        if (parsedMCQs.length === 0) {
            setError('No MCQs to import. Please preview first.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post('/api/mcqs/bulk', {
                mcqs: parsedMCQs.map(mcq => ({
                    ...mcq,
                    testId: selectedTestId
                }))
            });

            onImportComplete(response.data.results);
            handleClose();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to import MCQs');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setBulkText('');
        setSelectedTestId('');
        setDifficulty('medium');
        setCategory('General');
        setError('');
        setParsedMCQs([]);
        setShowPreview(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Upload />
                    Bulk Import MCQs
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Instructions */}
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <HelpOutline color="primary" />
                                <Typography variant="subtitle1">Format Instructions & Examples</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" paragraph>
                                <strong>Supported Formats:</strong>
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                <li>Questions can start with "Q1.", "Question:", or just be plain text</li>
                                <li>Options can use: A) B) C) D) or a) b) c) d) or 1. 2. 3. 4. or 1) 2) 3) 4)</li>
                                <li>Correct answer: "Correct: A", "Answer: a", "Correct Answer: 2"</li>
                                <li>Explanation: "Explanation: your explanation text" (optional)</li>
                                <li>Separate each MCQ with a blank line</li>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>Examples:</Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                                {exampleFormat}
                            </Paper>
                        </AccordionDetails>
                    </Accordion>

                    {/* Settings */}
                    <Box display="flex" gap={2} mb={3}>
                        <FormControl fullWidth>
                            <InputLabel>Select Test *</InputLabel>
                            <Select
                                value={selectedTestId}
                                label="Select Test *"
                                onChange={(e) => setSelectedTestId(e.target.value)}
                            >
                                {tests.map((test) => (
                                    <MenuItem key={test.id} value={test.id}>
                                        {test.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={difficulty}
                                label="Difficulty"
                                onChange={(e) => setDifficulty(e.target.value)}
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="easy">Easy</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="hard">Hard</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            sx={{ minWidth: 150 }}
                        />
                    </Box>

                    {/* Text Input */}
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        label="Paste your MCQs here"
                        placeholder="Paste your MCQ text here following the format shown in the examples above..."
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Box display="flex" gap={2} mb={3}>
                        <Button
                            variant="outlined"
                            onClick={handlePreview}
                            disabled={!bulkText.trim()}
                        >
                            Preview Parsed MCQs
                        </Button>
                        {parsedMCQs.length > 0 && (
                            <Chip
                                label={`${parsedMCQs.length} MCQ${parsedMCQs.length !== 1 ? 's' : ''} ready to import`}
                                color="success"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    {/* Preview */}
                    {showPreview && parsedMCQs.length > 0 && (
                        <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Preview ({parsedMCQs.length} MCQs parsed):
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {parsedMCQs.map((mcq, index) => (
                                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Q{index + 1}. {mcq.question}
                                    </Typography>

                                    {mcq.options.map((option, optIndex) => (
                                        <Typography
                                            key={optIndex}
                                            variant="body2"
                                            sx={{
                                                pl: 1,
                                                color: optIndex === mcq.correctAnswer ? 'success.main' : 'text.primary',
                                                fontWeight: optIndex === mcq.correctAnswer ? 'bold' : 'normal'
                                            }}
                                        >
                                            {String.fromCharCode(65 + optIndex)}) {option.text}
                                            {optIndex === mcq.correctAnswer && ' ✓'}
                                        </Typography>
                                    ))}

                                    {mcq.explanation && (
                                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                            Explanation: {mcq.explanation}
                                        </Typography>
                                    )}

                                    <Box sx={{ mt: 1 }}>
                                        <Chip label={mcq.difficulty} size="small" color="primary" variant="outlined" />
                                        <Chip label={mcq.category} size="small" variant="outlined" sx={{ ml: 1 }} />
                                    </Box>
                                </Box>
                            ))}
                        </Paper>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} startIcon={<Cancel />}>
                    Cancel
                </Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} /> : <Upload />}
                    disabled={loading || parsedMCQs.length === 0 || !selectedTestId}
                >
                    {loading ? 'Importing...' : `Import ${parsedMCQs.length} MCQs`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkMCQImport;
